import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MessagesSquare, X } from "lucide-react";

import { orpc } from "@/client";
import { AgoraSidePanel } from "@/components/today/agora-side-panel";
import { ActionQueue } from "@/components/today/action-queue";
import { OverlayShell } from "@/components/ui/overlay";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { SkeletonRows } from "@/components/ui/skeleton";
import { usePageVisible } from "@/hooks/use-page-visible";
import { AgoraLabels } from "@/lib/labels";
import { PollCadence } from "@/lib/poll-cadence";

export const Route = createFileRoute("/_app/")({
	component: TodayPage,
});

function OpenPanelButton({ onClick }: { onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="control-button button-outline min-h-11 px-3 py-2 xl:hidden"
		>
			<MessagesSquare className="size-4" />
			{AgoraLabels.panelTitle}
		</button>
	);
}

function PanelModal({ onClose }: { onClose: () => void }) {
	return (
		<OverlayShell
			label={AgoraLabels.panelTitle}
			onClose={onClose}
			className="items-center justify-center p-4 xl:hidden"
		>
			<div className="overlay-panel max-w-md p-4">
				<div className="flex justify-end">
					<Button
						type="button"
						size="sm"
						variant="ghost"
						onClick={onClose}
						aria-label="Fechar painel"
					>
						<X />
					</Button>
				</div>
				<AgoraSidePanel />
			</div>
		</OverlayShell>
	);
}

function TodayPage() {
	const { user } = Route.useRouteContext();
	const [panelOpen, setPanelOpen] = useState(false);
	const visible = usePageVisible();

	const {
		data: queue,
		isLoading,
		isError,
		isFetching,
		refetch,
	} = useQuery(
		orpc.today.queue.queryOptions({
			refetchInterval: () => PollCadence.pipeline({ visible }),
		}),
	);

	const { data: inbox } = useQuery(orpc.realtime.inbox.queryOptions());

	const summary =
		queue === undefined || inbox === undefined
			? undefined
			: {
					queue: queue.actions.length,
					live: inbox.cards.filter((card) => card.window_open).length,
				};

	return (
		<PageContainer>
			<PageHeader
				eyebrow={AgoraLabels.eyebrowDate(new Date())}
				title="Agora"
				actions={<OpenPanelButton onClick={() => setPanelOpen(true)} />}
			/>

			<div className="grid min-w-0 grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
				<section aria-label="Fila de ações" className="workspace-frame">
					<div className="workspace-section-heading">
						<h2 className="text-sm font-semibold">Pendências</h2>
						{!!summary && (
							<span className="text-xs tabular-nums text-muted-foreground">
								{AgoraLabels.queueCount(summary.queue)}
							</span>
						)}
					</div>
					{isLoading && <SkeletonRows rows={4} />}
					{isError && (
						<div role="alert" className="flex flex-col items-start gap-3 p-5">
							<p className="text-sm text-destructive">Não foi possível carregar as ações.</p>
							<Button
								type="button"
								size="sm"
								variant="outline"
								disabled={isFetching}
								onClick={() => refetch()}
							>
								Tentar novamente
							</Button>
						</div>
					)}
					{!!queue && (
						<ActionQueue
							queue={queue}
							showRole={user.user_type === "admin" || user.role === "staff"}
						/>
					)}
				</section>

				<aside className="sticky top-20 hidden min-w-0 self-start xl:block">
					<div className="mb-3 flex items-center gap-2 px-1 text-xs text-muted-foreground">
						<span className="size-1.5 rounded-full bg-success" />
						{!!summary && AgoraLabels.liveCount(summary.live)}
					</div>
					<div className="workspace-frame">
						<AgoraSidePanel />
					</div>
				</aside>
			</div>

			{panelOpen && <PanelModal onClose={() => setPanelOpen(false)} />}
		</PageContainer>
	);
}
